import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { ProjectSubtask, SubtaskFormData, TaskProgressSummary } from '../types';

export const useTaskManagement = (selectedProject: any, options?: { useElevatedWrites?: boolean }) => {
  const useElevatedWrites = !!options?.useElevatedWrites;

  const [subtasks, setSubtasks] = useState<ProjectSubtask[]>([]);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [projectTasks, setProjectTasks] = useState<any[]>([]);
  const [availableSteps, setAvailableSteps] = useState<{ step: number; step_name: string }[]>([
    { step: 1, step_name: 'Step 1' }
  ]);

  useEffect(() => {
    if (selectedProject) {
      loadProjectTasks();
    }
  }, [selectedProject]);

  useEffect(() => {
    if (selectedProject && selectedTaskId) {
      fetchSubtasks();
    } else {
      // Reset to default step when no task is selected
      setAvailableSteps([{ step: 1, step_name: 'Step 1' }]);
    }
  }, [selectedProject, selectedTaskId]);

  useEffect(() => {
    if (!selectedProject?.id || !selectedTaskId) return;

    const projectId = selectedProject.id;
    const channel = supabase
      .channel(`task-management-${projectId}-${selectedTaskId}-subtasks`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'project_subtasks',
          filter: `project_id=eq.${projectId}`,
        },
        async (payload: any) => {
          const updatedSubtask = payload.new;
          if (!updatedSubtask) return;

          let matchedVisibleSubtask = false;
          let nextVisibleSubtasks: ProjectSubtask[] | null = null;

          setSubtasks((currentSubtasks) => {
            matchedVisibleSubtask = currentSubtasks.some((subtask) => subtask.id === updatedSubtask.id);

            if (!matchedVisibleSubtask) {
              return currentSubtasks;
            }

            nextVisibleSubtasks = currentSubtasks.map((subtask) =>
              subtask.id === updatedSubtask.id
                ? {
                    ...subtask,
                    status: updatedSubtask.status as 'To Do' | 'In Progress' | 'Done',
                    updated_at: updatedSubtask.updated_at,
                    actual_hours: updatedSubtask.actual_hours ?? subtask.actual_hours,
                    step: updatedSubtask.step ?? subtask.step,
                    step_name: updatedSubtask.step_name ?? subtask.step_name,
                  }
                : subtask
            );

            return nextVisibleSubtasks;
          });

          if (nextVisibleSubtasks) {
            updateAvailableSteps(nextVisibleSubtasks);
          }

          if (!matchedVisibleSubtask) {
            await fetchSubtasks();
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'project_subtasks',
          filter: `project_id=eq.${projectId}`,
        },
        async () => {
          await fetchSubtasks();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'project_subtasks',
          filter: `project_id=eq.${projectId}`,
        },
        async () => {
          await fetchSubtasks();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedProject?.id, selectedTaskId]);

  const loadProjectTasks = () => {
    if (!selectedProject?.tasks) {
      setProjectTasks([]);
      return;
    }
    
    console.log('📋 TASK MANAGEMENT: Loading project tasks from project.tasks JSON:', {
      projectId: selectedProject.id,
      totalTasks: selectedProject.tasks.length,
      sampleTasks: selectedProject.tasks.slice(0, 3).map(t => ({
        taskId: t.taskId || t.name,
        name: t.name,
        duration: t.duration
      }))
    });

    // Convert project tasks and generate unique task IDs - exactly like Resource Management tab
    const tasksWithIds = selectedProject.tasks.map((task, index) => ({
      ...task,
      taskId: task.taskId || task.name || `Task-${index + 1}`,
      id: task.id || `task-${index + 1}`
    }));

    setProjectTasks(tasksWithIds);
    
    // Set the first task as selected if none is selected
    if (tasksWithIds.length > 0 && !selectedTaskId) {
      const firstTaskId = tasksWithIds[0].taskId || tasksWithIds[0].name;
      setSelectedTaskId(firstTaskId);
    }
  };

  const fetchSubtasks = async () => {
    if (!selectedProject || !selectedTaskId) return;
    
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      console.log('🔍 TASK MANAGEMENT: Fetching subtasks for:', {
        projectId: selectedProject.id,
        taskId: selectedTaskId,
        userId: user.id,
        userEmail: user.email
      });

      let allSubtasks: any[] = [];
      // Use edge function (service role) to resolve matching project task ids
      // and apply Team Lead / Manager hierarchical visibility rules.
      const { data: fnData, error: fnError } = await supabase.functions.invoke(
        'get-project-task-subtasks',
        {
          body: {
            project_id: selectedProject.id,
            task_name: selectedTaskId,
            scope: 'all',
            include_tasks: false,
          }
        }
      );

      if (fnError) {
        console.error('Error fetching subtasks via edge function:', fnError);
      } else {
        allSubtasks = (fnData as any)?.data ?? [];
      }

      console.log('✅ TASK MANAGEMENT: Fetched subtasks:', {
        total: allSubtasks.length
      });

      const typedSubtasks = allSubtasks.map(subtask => ({
        ...subtask,
        status: subtask.status as 'To Do' | 'In Progress' | 'Done',
        step: subtask.step || 1,
        step_name: subtask.step_name || 'Step 1'
      })) as ProjectSubtask[];

      setSubtasks(typedSubtasks);

      // Update available steps based on subtasks
      updateAvailableSteps(typedSubtasks);
    } catch (error) {
      console.error('Error fetching subtasks:', error);
      console.error('Failed to load subtasks:', (error as Error).message);
      setSubtasks([]);
      setAvailableSteps([{ step: 1, step_name: 'Step 1' }]);
    } finally {
      setIsLoading(false);
    }
  };

  const updateAvailableSteps = (currentSubtasks: ProjectSubtask[]) => {
    const stepsFromSubtasks = new Map<number, string>();
    
    // Collect unique steps from subtasks
    currentSubtasks.forEach(subtask => {
      const step = subtask.step || 1;
      const stepName = subtask.step_name || `Step ${step}`;
      stepsFromSubtasks.set(step, stepName);
    });

    // Ensure Step 1 always exists
    if (!stepsFromSubtasks.has(1)) {
      stepsFromSubtasks.set(1, 'Step 1');
    }

    // Convert to array and sort by step number
    const newAvailableSteps = Array.from(stepsFromSubtasks.entries())
      .map(([step, step_name]) => ({ step, step_name }))
      .sort((a, b) => a.step - b.step);

    setAvailableSteps(newAvailableSteps);
  };

  const resolveProjectTaskUUID = async (taskIdentifier: string) => {
    if (!selectedProject?.id || !taskIdentifier) {
      return null;
    }

    const getFirstMatch = async (column: 'name' | 'task_id') => {
      const { data, error } = await supabase
        .from('project_tasks')
        .select('id')
        .eq('project_id', selectedProject.id)
        .eq(column, taskIdentifier)
        .order('created_at', { ascending: true })
        .limit(1);

      if (error) {
        console.error(`❌ TASK MANAGEMENT: Error resolving project task by ${column}:`, error);
        return null;
      }

      return data?.[0]?.id ?? null;
    };

    return (await getFirstMatch('name')) || (await getFirstMatch('task_id'));
  };

  const calculateTaskProgress = async (taskId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 0;

      const resolvedTaskId = await resolveProjectTaskUUID(taskId);
      if (!resolvedTaskId) {
        console.error('Error finding project task for progress calculation:', taskId);
        return 0;
      }

      // Get all subtasks for this task (both owned and assigned by email)
      const { data: taskSubtasks, error: subtasksError } = await supabase
        .from('project_subtasks')
        .select('status')
        .eq('project_id', selectedProject.id)
        .eq('task_id', resolvedTaskId)
        .or(`user_id.eq.${user.id},email.eq.${user.email}`);

      if (subtasksError) {
        console.error('Error fetching subtasks for progress calculation:', subtasksError);
        return 0;
      }

      const totalSubtasks = taskSubtasks?.length || 0;
      const completedSubtasks = taskSubtasks?.filter(s => s.status === 'Done').length || 0;
      
      const progressPercentage = totalSubtasks > 0 ? Math.round((completedSubtasks / totalSubtasks) * 100) : 0;
      
      console.log('📊 TASK MANAGEMENT: Progress calculation:', {
        taskId,
        totalSubtasks,
        completedSubtasks,
        progressPercentage
      });

      return progressPercentage;
    } catch (error) {
      console.error('Error calculating task progress:', error);
      return 0;
    }
  };

  const updateProjectTaskProgress = async (taskId: string, progressPercentage: number) => {
    try {
      console.log('🔄 TASK MANAGEMENT: Updating project task progress:', {
        taskId,
        progressPercentage,
        projectId: selectedProject.id
      });

      // Update the project's tasks JSON with the new progress
      const updatedTasks = selectedProject.tasks.map(task => {
        const currentTaskId = task.taskId || task.name;
        if (currentTaskId === taskId) {
          return { ...task, progress: progressPercentage };
        }
        return task;
      });

      console.log('📝 TASK MANAGEMENT: Updated tasks array:', updatedTasks);

      // Update the project record with the modified tasks JSON - use correct column name
      const { error: updateError } = await supabase
        .from('projects')
        .update({ project_tasks: updatedTasks })
        .eq('id', selectedProject.id);

      if (updateError) {
        console.error('❌ TASK MANAGEMENT: Error updating project tasks:', updateError);
        return false;
      }

      console.log('✅ TASK MANAGEMENT: Project task progress updated successfully in database');
      
      // Update the local selectedProject object to trigger Gantt chart refresh
      if (selectedProject) {
        // Update the project's tasks array directly
        selectedProject.tasks = updatedTasks;
        
        // Force a complete re-render by updating the tasks array reference
        setProjectTasks([...updatedTasks]);
        
        console.log('🔄 TASK MANAGEMENT: Updated local project tasks for Gantt refresh');
        
        // Dispatch a custom event to notify other components about the update
        const customEvent = new CustomEvent('projectTasksUpdated', {
          detail: { 
            projectId: selectedProject.id, 
            tasks: updatedTasks,
            updatedTaskId: taskId,
            newProgress: progressPercentage
          }
        });
        
        window.dispatchEvent(customEvent);
        console.log('📡 TASK MANAGEMENT: Dispatched projectTasksUpdated event');
      }
      
      return true;
    } catch (error) {
      console.error('❌ TASK MANAGEMENT: Error updating project task progress:', error);
      return false;
    }
  };

  const saveSubtask = async (formData: SubtaskFormData) => {
    if (!selectedProject || !selectedTaskId) {
      console.error('❌ TASK MANAGEMENT: Missing project or task for subtask creation');
      console.error('Error: Missing project or task information');
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('❌ TASK MANAGEMENT: No authenticated user found');
        console.error('Please log in to create subtasks');
        return;
      }

      console.log('💾 TASK MANAGEMENT: Creating subtask with form data', {
        formData,
        selectedProject: selectedProject.id,
        selectedTaskId,
        userId: user.id
      });

      // Validate that formData.resource_id is a UUID (should be assignment ID)
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!formData.resource_id) {
        console.error('Error: No resource selected');
        return;
      }

      if (!uuidRegex.test(formData.resource_id)) {
        console.error('❌ TASK MANAGEMENT: Invalid assignment ID format:', formData.resource_id);
        console.error('Error: Invalid resource selection. Please select a valid resource from the dropdown.');
        return;
      }

      let assignmentData: { resource_id: string; name: string; role: string } | null = null;
      let projectTaskUUID: string | null = null;

      if (!useElevatedWrites) {
        console.log('🔍 TASK MANAGEMENT: Looking up resource assignment with ID:', formData.resource_id);
        const { data: ad, error: assignmentError } = await supabase
          .from('resource_assignment')
          .select('resource_id, name, role')
          .eq('id', formData.resource_id)
          .single();

        if (assignmentError || !ad?.resource_id) {
          console.error('❌ TASK MANAGEMENT: Error fetching resource assignment:', assignmentError);
          return;
        }
        assignmentData = ad as any;

        const resolvedTaskId = await resolveProjectTaskUUID(selectedTaskId);
        if (!resolvedTaskId) {
          console.error('❌ TASK MANAGEMENT: Error fetching project task:', selectedTaskId);
          return;
        }
        projectTaskUUID = resolvedTaskId;
      }

      // Base subtask payload (excluding fields resolved server-side when elevated)
      const subtaskData: any = {
        project_id: selectedProject.id,
        user_id: user.id,
        subtask_name: formData.subtask_name,
        description: formData.description || null,
        status: formData.status,
        estimated_hours: formData.estimated_hours,
        actual_hours: 0,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        step: formData.step || 1,
        step_name: formData.step_name || 'Step 1',
        email: formData.email || null,
      };

      if (!useElevatedWrites) {
        subtaskData.task_id = projectTaskUUID;
        subtaskData.resource_id = assignmentData!.resource_id;
        subtaskData.name = assignmentData!.name;
        subtaskData.role = assignmentData!.role;
      }

      console.log('💾 TASK MANAGEMENT: Creating subtask with data:', subtaskData);

      let data: any = null;
      let error: any = null;
      if (useElevatedWrites) {
        const { data: fnData, error: fnError } = await supabase.functions.invoke(
          'manage-subtask-elevated',
          {
            body: {
              action: 'insert',
              project_id: selectedProject.id,
              payload: subtaskData,
              assignment_id: formData.resource_id,
              task_name: selectedTaskId,
            },
          }
        );
        data = (fnData as any)?.data ?? null;
        error = fnError || ((fnData as any)?.error ? new Error((fnData as any).error) : null);
      } else {
        const res = await supabase
          .from('project_subtasks')
          .insert(subtaskData)
          .select()
          .single();
        data = res.data;
        error = res.error;
      }

      if (error) {
        console.error('❌ TASK MANAGEMENT: Error creating subtask:', error);
        console.error('Failed to create subtask:', (error as any).message);
        return;
      }



      
      console.log('✅ TASK MANAGEMENT: Subtask created successfully:', data);
      // Remove the success alert as requested by user
      fetchSubtasks();

      // Recalculate and update task progress after creating new subtask
      if (selectedTaskId) {
        const newProgress = await calculateTaskProgress(selectedTaskId);
        await updateProjectTaskProgress(selectedTaskId, newProgress);
      }
    } catch (error) {
      console.error('❌ TASK MANAGEMENT: Error saving subtask:', error);
      console.error('Failed to save subtask:', (error as Error).message);
      throw error;
    }
  };

  const updateSubtask = async (subtaskId: string, formData: SubtaskFormData) => {
    try {
      console.log('📝 TASK MANAGEMENT: Updating subtask:', subtaskId, formData);

      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      const isUuid = uuidRegex.test(formData.resource_id || '');

      // Try to look up the resource_assignment by id; if not found we'll
      // preserve the existing resource_id/name/role on the subtask.
      let assignmentData: { resource_id: string; name: string; role: string } | null = null;
      if (isUuid) {
        const { data, error } = await supabase
          .from('resource_assignment')
          .select('resource_id, name, role')
          .eq('id', formData.resource_id)
          .maybeSingle();
        if (!error && data) {
          assignmentData = data as any;
        }
      }

      const baseUpdate: any = {
        subtask_name: formData.subtask_name,
        description: formData.description || null,
        status: formData.status,
        estimated_hours: formData.estimated_hours,
        actual_hours: formData.actual_hours || null,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        step: formData.step || 1,
        step_name: formData.step_name || 'Step 1',
        email: formData.email || null,
      };

      const updateData = assignmentData
        ? {
            ...baseUpdate,
            resource_id: assignmentData.resource_id,
            name: assignmentData.name,
            role: assignmentData.role,
          }
        : baseUpdate;

      let error: any = null;
      if (useElevatedWrites) {
        const { data: fnData, error: fnError } = await supabase.functions.invoke(
          'manage-subtask-elevated',
          { body: { action: 'update', project_id: selectedProject.id, subtask_id: subtaskId, payload: updateData, assignment_id: isUuid ? formData.resource_id : undefined } }
        );

        error = fnError || ((fnData as any)?.error ? new Error((fnData as any).error) : null);
      } else {
        const res = await supabase
          .from('project_subtasks')
          .update(updateData)
          .eq('id', subtaskId);
        error = res.error;
      }

      if (error) {
        console.error('❌ TASK MANAGEMENT: Error updating subtask:', error);
        throw new Error('Failed to update subtask: ' + (error as any).message);
      }

      
      console.log('✅ TASK MANAGEMENT: Subtask updated successfully');
      
      // Refresh subtasks first
      await fetchSubtasks();

      // If status changed, recalculate and update task progress
      if (selectedTaskId) {
        const newProgress = await calculateTaskProgress(selectedTaskId);
        await updateProjectTaskProgress(selectedTaskId, newProgress);
      }
    } catch (error) {
      console.error('❌ TASK MANAGEMENT: Error updating subtask:', error);
      console.error('Failed to update subtask:', (error as Error).message);
      throw error;
    }
  };

  const deleteSubtask = async (subtaskId: string) => {
    try {
      console.log('🗑️ TASK MANAGEMENT: Deleting subtask:', subtaskId);

      let error: any = null;
      if (useElevatedWrites && selectedProject) {
        const { data: fnData, error: fnError } = await supabase.functions.invoke(
          'manage-subtask-elevated',
          { body: { action: 'delete', project_id: selectedProject.id, subtask_id: subtaskId } }
        );
        error = fnError || ((fnData as any)?.error ? new Error((fnData as any).error) : null);
      } else {
        const res = await supabase
          .from('project_subtasks')
          .delete()
          .eq('id', subtaskId);
        error = res.error;
      }

      if (error) {
        console.error('❌ TASK MANAGEMENT: Error deleting subtask:', error);
        console.error('Failed to delete subtask:', (error as any).message);
        throw error;
      }


      console.log('✅ TASK MANAGEMENT: Subtask deleted successfully');
      // Removed the browser alert here
      
      // Refresh subtasks first
      await fetchSubtasks();

      // Recalculate and update task progress after deletion
      if (selectedTaskId) {
        const newProgress = await calculateTaskProgress(selectedTaskId);
        await updateProjectTaskProgress(selectedTaskId, newProgress);
      }
    } catch (error) {
      console.error('❌ TASK MANAGEMENT: Error deleting subtask:', error);
      console.error('Failed to delete subtask:', (error as Error).message);
    }
  };

  const updateSubtaskStatus = async (subtaskId: string, status: 'To Do' | 'In Progress' | 'Done') => {
    try {
      console.log('🔄 TASK MANAGEMENT: Updating subtask status:', subtaskId, status);

      const { data, error } = await supabase.functions.invoke(
        'update-subtask-status-elevated',
        { body: { subtask_id: subtaskId, status } }
      );

      if (error || (data as any)?.error) {
        const msg = (error as any)?.message || (data as any)?.error || 'Update failed';
        console.error('❌ TASK MANAGEMENT: Error updating subtask status:', msg);
        throw new Error(msg);
      }

      const resp = (data as any) || {};
      console.log('✅ TASK MANAGEMENT: Subtask status updated successfully', resp);

      await fetchSubtasks();

      // Apply server-computed progress to local project + Gantt
      if (selectedProject && Array.isArray(resp.updated_tasks)) {
        selectedProject.tasks = resp.updated_tasks;
        setProjectTasks([...resp.updated_tasks]);
        window.dispatchEvent(new CustomEvent('projectTasksUpdated', {
          detail: {
            projectId: selectedProject.id,
            tasks: resp.updated_tasks,
            updatedTaskName: resp.task_name,
            newProgress: resp.progress,
          },
        }));
      }
    } catch (error) {
      console.error('❌ TASK MANAGEMENT: Error updating subtask status:', error);
      console.error('Failed to update subtask status:', (error as Error).message);
    }
  };

  const moveSubtask = async (subtaskId: string, newStep: number, newStepName: string) => {
    try {
      console.log('🔄 TASK MANAGEMENT: Moving subtask to new step:', {
        subtaskId,
        newStep,
        newStepName
      });

      let error: any = null;
      if (useElevatedWrites && selectedProject) {
        const { data: fnData, error: fnError } = await supabase.functions.invoke(
          'manage-subtask-elevated',
          { body: { action: 'move', project_id: selectedProject.id, subtask_id: subtaskId, step: newStep, step_name: newStepName } }
        );
        error = fnError || ((fnData as any)?.error ? new Error((fnData as any).error) : null);
      } else {
        const res = await supabase
          .from('project_subtasks')
          .update({ step: newStep, step_name: newStepName })
          .eq('id', subtaskId);
        error = res.error;
      }

      if (error) {
        console.error('❌ TASK MANAGEMENT: Error moving subtask:', error);
        console.error('Failed to move subtask:', (error as any).message);
        throw error;
      }


      console.log('✅ TASK MANAGEMENT: Subtask moved successfully');
      await fetchSubtasks();
    } catch (error) {
      console.error('❌ TASK MANAGEMENT: Error moving subtask:', error);
      console.error('Failed to move subtask:', (error as Error).message);
    }
  };

  const addStep = async (stepName: string) => {
    try {
      // Find the next step number
      const maxStep = Math.max(...availableSteps.map(s => s.step), 0);
      const newStepNumber = maxStep + 1;

      console.log('➕ TASK MANAGEMENT: Adding new step:', {
        stepName,
        stepNumber: newStepNumber
      });

      // Add the new step to available steps
      const newStep = { step: newStepNumber, step_name: stepName };
      setAvailableSteps(prev => [...prev, newStep].sort((a, b) => a.step - b.step));

      console.log('✅ TASK MANAGEMENT: Step added successfully');
    } catch (error) {
      console.error('❌ TASK MANAGEMENT: Error adding step:', error);
      console.error('Failed to add step:', (error as Error).message);
    }
  };

  const editStep = async (stepNumber: number, newStepName: string) => {
    try {
      console.log('✏️ TASK MANAGEMENT: Editing step:', {
        stepNumber,
        newStepName
      });

      // Update all subtasks in this step with the new step name
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !selectedProject || !selectedTaskId) return;

      // Get the project task UUID
      const resolvedTaskId = await resolveProjectTaskUUID(selectedTaskId);

      if (!resolvedTaskId) {
        console.error('Error finding project task for step edit:', selectedTaskId);
        return;
      }

      // Update all subtasks in this step
      let error: any = null;
      if (useElevatedWrites) {
        const { data: fnData, error: fnError } = await supabase.functions.invoke(
          'manage-subtask-elevated',
          { body: { action: 'edit_step', project_id: selectedProject.id, task_id: resolvedTaskId, step: stepNumber, step_name: newStepName } }
        );
        error = fnError || ((fnData as any)?.error ? new Error((fnData as any).error) : null);
      } else {
        const res = await supabase
          .from('project_subtasks')
          .update({ step_name: newStepName })
          .eq('project_id', selectedProject.id)
          .eq('task_id', resolvedTaskId)
          .eq('user_id', user.id)
          .eq('step', stepNumber);
        error = res.error;
      }

      if (error) {
        console.error('❌ TASK MANAGEMENT: Error updating step name:', error);
        console.error('Failed to update step name:', (error as any).message);
        throw error;
      }


      // Update local available steps
      setAvailableSteps(prev => 
        prev.map(step => 
          step.step === stepNumber 
            ? { ...step, step_name: newStepName }
            : step
        )
      );

      console.log('✅ TASK MANAGEMENT: Step name updated successfully');
      await fetchSubtasks();
    } catch (error) {
      console.error('❌ TASK MANAGEMENT: Error editing step:', error);
      console.error('Failed to edit step:', (error as Error).message);
    }
  };

  const deleteStep = async (step: number) => {
    try {
      if (step === 1) {
        console.error('Cannot delete Step 1. At least one step must remain.');
        return;
      }

      const subtasksInStep = subtasks.filter(s => s.step === step);
      
      if (subtasksInStep.length > 0) {
        // Automatically move all subtasks in this step to Step 1
        const movePromises = subtasksInStep.map(subtask => 
          moveSubtask(subtask.id, 1, 'Step 1')
        );
        await Promise.all(movePromises);
      }

      // Remove the step from available steps
      setAvailableSteps(prev => prev.filter(s => s.step !== step));

      console.log('🗑️ TASK MANAGEMENT: Step deleted successfully');
      await fetchSubtasks();
    } catch (error) {
      console.error('❌ TASK MANAGEMENT: Error deleting step:', error);
      console.error('Failed to delete step:', (error as Error).message);
    }
  };

  const getExistingSteps = () => {
    return availableSteps;
  };

  // Get unique task IDs from project tasks - exactly like Resource Management tab
  const getUniqueTaskIds = (): string[] => {
    return projectTasks.map(task => task.taskId || task.name).filter(Boolean);
  };

  return {
    subtasks,
    selectedTaskId,
    setSelectedTaskId,
    isLoading,
    projectTasks,
    uniqueTaskIds: getUniqueTaskIds(),
    saveSubtask,
    updateSubtask,
    deleteSubtask,
    updateSubtaskStatus,
    fetchSubtasks,
    moveSubtask,
    addStep,
    editStep,
    deleteStep,
    getExistingSteps,
    availableSteps
  };
};
